import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { LayoutDashboard, Users2, MessageSquare, LogOut, PhoneCall, BarChart3, Signal, Megaphone, Menu, X } from "lucide-react";

const STATUS_ROWS = [
  { key: "new", label: "New", dot: "bg-zinc-400" },
  { key: "not_connected", label: "Not Connected", dot: "bg-zinc-500" },
  { key: "follow_up", label: "Follow-up", dot: "bg-blue-500" },
  { key: "hold", label: "Hold", dot: "bg-amber-500" },
  { key: "completed", label: "Completed", dot: "bg-emerald-500" },
  { key: "rejected", label: "Rejected", dot: "bg-red-500" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isTelecaller = user?.role === "telecaller";

  useEffect(() => {
    if (!user || !isTelecaller) return;
    api.get("/reports/summary").then((r) => setSummary(r.data)).catch(() => {});
  }, [user, isTelecaller, location.pathname]);

  const loadUnread = useCallback(() => {
    if (!isTelecaller) return;
    api.get("/notices/unread-count").then((r) => setUnread(r.data.count || 0)).catch(() => {});
  }, [isTelecaller]);

  useEffect(() => {
    loadUnread();
    const h = () => loadUnread();
    window.addEventListener("notices-updated", h);
    return () => window.removeEventListener("notices-updated", h);
  }, [loadUnread, location.pathname]);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const filterByStatus = (statusKey) => { navigate(`/my-leads?status=${statusKey}`); };

  const adminNav = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/leads", label: "Leads", icon: MessageSquare },
    { to: "/admin/telecallers", label: "Telecallers", icon: Users2 },
    { to: "/admin/notices", label: "Notices", icon: Megaphone },
    { to: "/admin/reports", label: "Reports", icon: BarChart3 },
  ];
  const tcNav = [
    { to: "/my-leads", label: "My Leads", icon: PhoneCall, end: true },
    { to: "/notices", label: "Notices", icon: Megaphone, badge: unread },
  ];
  const nav = isTelecaller ? tcNav : adminNav;

  const SidebarContent = () => (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-zinc-800 px-5 py-5">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-emerald-500 text-black">
            <Signal className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="font-heading text-[15px] font-bold tracking-tight text-white">
            CRM<span className="text-emerald-400">rogveda</span>
          </div>
        </div>
        <button onClick={() => setMobileOpen(false)} className="lg:hidden text-zinc-400 hover:text-white" aria-label="Close menu">
          <X className="h-5 w-5"/>
        </button>
      </div>

      <nav className="space-y-0.5 p-3">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center justify-between gap-2.5 rounded-sm px-3 py-2 text-[13px] transition-colors ${
                isActive ? "bg-white text-black font-semibold" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
              }`
            }>
            <span className="flex items-center gap-2.5"><n.icon className="h-4 w-4" />{n.label}</span>
            {n.badge > 0 && <span data-testid="nav-notice-badge" className="rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-black">{n.badge}</span>}
          </NavLink>
        ))}
      </nav>

      {isTelecaller && summary && (
        <div className="flex-1 overflow-y-auto border-t border-zinc-800 p-3" data-testid="tc-sidebar-stats">
          <div className="mb-2 px-2 text-[10px] uppercase tracking-widest text-zinc-500">My Remarks</div>
          <div className="space-y-1">
            {STATUS_ROWS.map((r) => {
              const count = summary.status_counts?.[r.key] || 0;
              return (
                <button key={r.key} data-testid={`tc-sidebar-${r.key}`} onClick={() => filterByStatus(r.key)}
                  className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left hover:bg-zinc-900 transition-colors">
                  <span className="flex items-center gap-2 text-[12px] text-zinc-300">
                    <span className={`h-1.5 w-1.5 rounded-full ${r.dot}`} />
                    {r.label}
                  </span>
                  <span className="tabular-nums text-[12px] font-semibold text-white">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 rounded-sm border border-zinc-800 bg-zinc-900 p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Sales closed</div>
            <div className="mt-1 font-heading text-lg font-bold text-emerald-400">₹{Number(summary.total_sales || 0).toLocaleString("en-IN")}</div>
            <div className="text-[10px] text-zinc-500">{summary.completed_count} deals · {summary.conversion_rate}% conversion</div>
          </div>
        </div>
      )}

      {!isTelecaller && <div className="flex-1"/>}

      <div className="border-t border-zinc-800 p-3">
        <div className="mb-2 px-2 py-1">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">{user?.role}</div>
          <div className="truncate text-sm font-medium text-white">{user?.name}</div>
          <div className="truncate text-xs text-zinc-500">{user?.email}</div>
        </div>
        <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }}
          className="flex w-full items-center gap-2 rounded-sm border border-zinc-800 px-3 py-2 text-xs text-zinc-300 hover:bg-zinc-900">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#F4F4F5] font-body">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 flex-col border-r border-zinc-800 bg-zinc-950 text-zinc-300">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="lg:hidden fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-zinc-950 text-zinc-300 shadow-2xl">
            <SidebarContent />
          </aside>
        </>
      )}

      <main className="flex-1 overflow-auto">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-zinc-200 bg-white px-4 py-3">
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Open menu"
            className="rounded-sm border border-zinc-300 p-1.5 hover:bg-zinc-50">
            <Menu className="h-5 w-5"/>
          </button>
          <div className="flex items-center gap-1.5">
            <div className="flex h-6 w-6 items-center justify-center rounded-sm bg-emerald-500 text-black">
              <Signal className="h-3 w-3" strokeWidth={3} />
            </div>
            <span className="font-heading text-sm font-bold">CRM<span className="text-emerald-600">rogveda</span></span>
          </div>
          <div className="w-9">
            {isTelecaller && unread > 0 && (
              <span className="rounded-full bg-emerald-500 px-1.5 py-0.5 text-[10px] font-bold text-black">{unread}</span>
            )}
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
